<template>
  <app-card
    body-class="space-y-6"
    :border-color="isPayable ? 'neutral' : 'error'"
  >
    <div class="flex justify-between gap-3">
      <user-badge
        :is-loading="isLoading"
        :user-name="reservation?.supplier?.name"
        data-sentry-unmask
      >
        <template #description>
          <app-icon
            size="sm"
            name="calendar"
          />
          <p>{{ formatDate(reservation?.start_date) }} - {{ formatDate(reservation?.end_date) }}</p>
        </template>
      </user-badge>
      <app-icon
        v-if="!isLoading"
        size="sm"
        variant="solid"
        :name="isPayable ? 'check' : 'alert-circle'"
        :color="isPayable ? 'success' : 'error'"
      />
    </div>

    <div class="space-y-3">
      <data-property
        label="Traveler"
        icon="user"
        direction="horizontal"
        :value="abbreviateName(reservation?.traveler_first_name, reservation?.traveler_last_name)"
        :is-loading="isLoading"
      />
      <data-property
        label="Agency"
        icon="building"
        direction="horizontal"
        :value="reservation?.agency?.name"
        :is-loading="isLoading"
        :is-invalid="!isPayable"
        data-sentry-unmask
      />
      <data-property
        label="Commission"
        icon="coin-hand"
        direction="horizontal"
        :value="
          formatNumber({
            value: reservation?.commission || 0,
            options: { style: 'currency', currency: reservation?.currency_alpha_code || 'USD' },
          })
        "
        :is-loading="isLoading"
      />
    </div>

    <slot name="actions">
      <template v-if="isActionable">
        <div
          class="grid grid-cols-2 gap-3"
          data-cy="reservation-drawer-action-buttons"
        >
          <app-dropdown
            v-if="canDeferAndDeleteReservation"
            content-class="w-max min-w-50"
          >
            <button class="btn btn-sm btn-secondary w-full">Defer or delete</button>
            <template #content>
              <list-item
                size="sm"
                v-if="isStatementOpened"
                @click="showDeferReservationModal"
              >
                <app-icon
                  name="flip-forward"
                  size="sm"
                />
                Defer reservation
              </list-item>
              <list-item
                size="sm"
                @click="showDeleteReservationModal"
              >
                <app-icon
                  name="trash"
                  size="sm"
                />
                Delete reservation
              </list-item>
            </template>
          </app-dropdown>
          <router-link
            v-if="canEditReservation"
            class="btn btn-sm btn-secondary"
            :to="{
              name: 'statement-details-reservation-edit',
              params: { reservationId: reservation.id },
            }"
          >
            <app-icon
              name="edit"
              size="sm"
            />
            Edit details
          </router-link>
        </div>
      </template>
    </slot>

    <template v-if="isActionable && !isPayable">
      <app-alert
        icon="info-circle"
        heading="This reservation is not payable"
      >
        <p>Review errors listed below or defer payment to review this statement.</p>
      </app-alert>

      <status-message
        color="error"
        message="Billing information missing"
        direction="vertical"
      >
        <p>Waiting for {{ agencyName }} to verify it's beneficiary information.</p>
      </status-message>
    </template>
  </app-card>

  <defer-reservation-modal
    ref="deferReservationModalRef"
    @defer:success="emit('defer:success')"
  />
  <delete-reservation-modal
    ref="deleteReservationModalRef"
    :statement-id="statement.id"
    :reservation-id="reservation.id"
    @delete:success="emit('delete:success')"
  />
</template>

<script setup>
import { computed, inject, ref } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { formatDate } from '@/utils/dates';
import { formatNumber } from '@/utils/number';
import { abbreviateName } from '@/utils/string';
import { StatementStatus } from '@/constants/statements';

import UserBadge from '@/components/sidebar/UserBadge.vue';
import ListItem from '@/components/ui/list/ListItem.vue';
import DeleteReservationModal from '@/components/reservations/DeleteReservationModal.vue';
import DeferReservationModal from '@/components/reservations/DeferReservation/Modal.vue';

const props = defineProps({
  isActionable: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const statement = inject('statement');
const reservation = inject('reservation');

const emit = defineEmits(['delete:success', 'defer:success']);

const deleteReservationModalRef = ref(null);
const deferReservationModalRef = ref(null);

const showDeleteReservationModal = () => {
  deleteReservationModalRef.value?.showModal();
};

const showDeferReservationModal = () => {
  deferReservationModalRef.value?.showModal();
};

const authStore = useAuthStore();
const canDeferAndDeleteReservation = computed(() =>
  authStore.hasAllPermissions(['reservation:defer', 'reservation:delete'])
);
const canEditReservation = computed(() => authStore.hasPermission('reservation:edit'));
const isStatementOpened = computed(() => statement.value?.status === StatementStatus.OPENED);

const agencyName = computed(() => reservation.value?.agency?.name);
const isPayable = computed(() => {
  if (props.isLoading || !statement) return true;
  return !!reservation.value?.agency_identifier?.transfer_party;
});
</script>
